/**
 * PmsHousekeeping — mobile-first housekeeping board.
 *
 * Estado local por villa: PENDING → IN_PROGRESS → READY → INSPECTED.
 * Initial state se deriva de las salidas/llegadas del día (selectors).
 *
 * "Reportar incidencia" abre un modal con formulario real. Al crear, la
 * incidencia se registra en el store compartido `pmsRuntimeStore`. Si
 * bloquea inventario, también crea un availability block MAINTENANCE que
 * aparece inmediatamente en /pms/issues, /pms/calendar y /pms/allocation
 * sin recargar la página.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import {
  CheckCircle, MagnifyingGlass, ArrowRight, Warning, Clock, Play, X, Plus,
  Camera,
} from "@phosphor-icons/react";
import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { housekeepingTasksForDay, getChannel, getVilla } from "@/lib/pmsSelectors";
import { createIssue } from "@/lib/pmsRuntimeStore";
import { addDays } from "@/lib/pmsSelectors";

const STATUS_ORDER = ["PENDING", "IN_PROGRESS", "READY", "INSPECTED"];

const STATUS_STYLE = {
  PENDING:     { badge: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",  dot: "bg-amber-500" },
  IN_PROGRESS: { badge: "bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200",           dot: "bg-sky-500" },
  READY:       { badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200", dot: "bg-emerald-500" },
  INSPECTED:   { badge: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-200",       dot: "bg-stone-500" },
};

const STATUS_LABEL = {
  PENDING:     "Pendiente",
  IN_PROGRESS: "En curso",
  READY:       "Lista",
  INSPECTED:   "Inspeccionada",
};

const PRIORITY_LABEL = {
  urgent: "Urgente",
  high:   "Alta",
  medium: "Media",
  low:    "Baja",
};

const PRIORITY_STYLE = {
  urgent: "text-red-800 dark:text-red-200",
  high:   "text-red-700 dark:text-red-300",
  medium: "text-amber-700 dark:text-amber-300",
  low:    "text-stone-500 dark:text-stone-400",
};

const CATEGORY_OPTIONS = [
  { key: "cleaning",     label: "Limpieza" },
  { key: "maintenance",  label: "Mantenimiento" },
  { key: "damage",       label: "Daño" },
  { key: "missing_item", label: "Falta material" },
  { key: "other",        label: "Otros" },
];

const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];

function nextStatus(s) {
  const i = STATUS_ORDER.indexOf(s);
  return i >= 0 && i < STATUS_ORDER.length - 1 ? STATUS_ORDER[i + 1] : s;
}

export default function PmsHousekeeping() {
  const tasks = useMemo(() => housekeepingTasksForDay(DEMO_TODAY_ISO), []);

  // Local state — status por villa (una tarea/villa/día en el demo).
  const [state, setState] = useState(() => {
    const map = {};
    for (const t of tasks) {
      map[t.villa.villa_id] = { status: t.initial_status, lastIssueId: null };
    }
    return map;
  });

  // Modal state (una incidencia a la vez).
  const [modal, setModal] = useState(null); // { villa_id, reservation_id }

  const advance = (villaId) => {
    setState((cur) => ({
      ...cur,
      [villaId]: { ...cur[villaId], status: nextStatus(cur[villaId].status) },
    }));
  };

  const setStatus = (villaId, status) => {
    setState((cur) => ({ ...cur, [villaId]: { ...cur[villaId], status } }));
  };

  const openIncidentModal = (villaId, reservationId) => {
    setModal({ villa_id: villaId, reservation_id: reservationId || null });
  };

  const handleIssueCreated = (villaId, issue) => {
    setState((cur) => ({
      ...cur,
      [villaId]: { ...cur[villaId], lastIssueId: issue.issue_id },
    }));
    setModal(null);
  };

  const counts = useMemo(() => {
    const c = { PENDING: 0, IN_PROGRESS: 0, READY: 0, INSPECTED: 0 };
    for (const t of tasks) c[state[t.villa.villa_id]?.status]++;
    return c;
  }, [state, tasks]);

  return (
    <PmsShell>
      <Section
        title="Housekeeping · Hoy"
        subtitle={`Turnaround del día ${DEMO_TODAY_ISO}. Estado local — las incidencias creadas se comparten con /pms/issues.`}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          {STATUS_ORDER.map((s) => (
            <div key={s} className="dv-card text-center py-3">
              <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_STYLE[s].badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_STYLE[s].dot}`} />
                {STATUS_LABEL[s]}
              </div>
              <div className="mt-1 text-2xl font-semibold text-stone-900 dark:text-stone-100 tabular-nums">
                {counts[s] || 0}
              </div>
            </div>
          ))}
        </div>

        {tasks.length === 0 ? (
          <div className="dv-card text-center py-10 text-sm text-stone-500 dark:text-stone-400">
            No hay tareas de limpieza para hoy.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="pms-housekeeping-cards">
            {tasks.map((t) => (
              <HousekeepingCard
                key={t.villa.villa_id}
                task={t}
                st={state[t.villa.villa_id]}
                onAdvance={() => advance(t.villa.villa_id)}
                onSetStatus={(s) => setStatus(t.villa.villa_id, s)}
                onReportIncident={() => openIncidentModal(
                  t.villa.villa_id,
                  t.next_res?.reservation_id || t.checkout_res?.reservation_id || null,
                )}
              />
            ))}
          </div>
        )}
      </Section>

      {modal && (
        <IncidentModal
          initial={modal}
          onClose={() => setModal(null)}
          onCreated={(issue) => handleIssueCreated(modal.villa_id, issue)}
        />
      )}
    </PmsShell>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function HousekeepingCard({ task, st, onAdvance, onSetStatus, onReportIncident }) {
  const status = st?.status || "PENDING";
  const style = STATUS_STYLE[status];
  const nextRes = task.next_res;
  const outRes = task.checkout_res;
  const lastIssueId = st?.lastIssueId;
  return (
    <div
      className="dv-card border-l-4"
      style={{ borderLeftColor: task.priority === "high" ? "#ef4444" : task.priority === "medium" ? "#f59e0b" : "#a8a29e" }}
      data-testid={`pms-hk-card-${task.villa.villa_id}`}
      data-status={status}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] text-stone-400 tracking-widest">{task.villa.villa_id}</div>
          <div className="font-medium text-stone-900 dark:text-stone-100">{task.villa.name}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${style.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {STATUS_LABEL[status]}
        </span>
      </div>

      <ul className="mt-3 text-xs text-stone-700 dark:text-stone-300 space-y-1">
        {outRes ? (
          <li className="inline-flex items-center gap-1.5">
            <Clock size={12} className="text-stone-400" /> Checkout 11:00 · {outRes.reservation_id}
          </li>
        ) : (
          <li className="text-stone-400">Sin checkout hoy</li>
        )}
        {nextRes ? (
          <li className="inline-flex items-center gap-1.5">
            <ArrowRight size={12} className="text-stone-400" /> Check-in 14:00 · {nextRes.reservation_id} · {getChannel(nextRes.channel).label}
          </li>
        ) : (
          <li className="text-stone-400">Sin check-in hoy</li>
        )}
        <li className={`inline-flex items-center gap-1.5 ${PRIORITY_STYLE[task.priority]}`}>
          <Warning size={12} /> Prioridad: {PRIORITY_LABEL[task.priority]}
          {task.turnaround_hours != null && (
            <span className="ml-2 text-stone-400">· turnaround {task.turnaround_hours}h</span>
          )}
        </li>
      </ul>

      {lastIssueId && (
        <div className="mt-3 rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-2 text-[11px] text-emerald-800 dark:text-emerald-200 flex items-center justify-between gap-2">
          <span>
            Incidencia <span className="font-mono">{lastIssueId}</span> creada
          </span>
          <Link
            to="/pms/issues"
            className="inline-flex items-center gap-1 text-emerald-900 dark:text-emerald-100 hover:underline"
          >
            Ver en Incidencias <ArrowRight size={10} />
          </Link>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {status === "PENDING" && (
          <ActionBtn onClick={onAdvance} icon={Play} primary>Empezar limpieza</ActionBtn>
        )}
        {status === "IN_PROGRESS" && (
          <ActionBtn onClick={() => onSetStatus("READY")} icon={CheckCircle} primary>Marcar lista</ActionBtn>
        )}
        {status === "READY" && (
          <ActionBtn onClick={() => onSetStatus("INSPECTED")} icon={MagnifyingGlass} primary>Inspeccionar</ActionBtn>
        )}
        {status === "INSPECTED" && (
          <span className="text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> Cerrada
          </span>
        )}
        <ActionBtn onClick={onReportIncident} icon={Warning}>Reportar incidencia</ActionBtn>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, primary, children }) {
  const cls = primary
    ? "bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90"
    : "border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-500";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5 min-h-[36px] ${cls}`}
    >
      {Icon && <Icon size={12} weight="bold" />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Incident modal — form real, crea issue en el store compartido
// ---------------------------------------------------------------------------

function IncidentModal({ initial, onClose, onCreated }) {
  const villa = getVilla(initial.villa_id);
  const [category, setCategory] = useState("maintenance");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [blocksInventory, setBlocksInventory] = useState(false);
  const [blockStart, setBlockStart] = useState(DEMO_TODAY_ISO);
  const [blockEnd, setBlockEnd] = useState(addDays(DEMO_TODAY_ISO, 1));
  const [reservationId] = useState(initial.reservation_id || "");
  const [photoAttached, setPhotoAttached] = useState(false);
  const [error, setError] = useState("");

  const datesValid = !blocksInventory || (blockStart && blockEnd && blockEnd > blockStart);
  const canSubmit = description.trim().length > 0 && datesValid;

  const submit = (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError("La descripción es obligatoria.");
      return;
    }
    if (blocksInventory && !datesValid) {
      setError("Las fechas del bloqueo son inválidas (fin debe ser posterior al inicio).");
      return;
    }
    const { issue } = createIssue({
      villa_id: initial.villa_id,
      category,
      description: description.trim(),
      title: description.trim().split("\n")[0].slice(0, 80),
      priority,
      blocks_inventory: blocksInventory,
      reservation_id: reservationId || null,
      photos_placeholder: photoAttached ? 1 : 0,
      block_start_date: blocksInventory ? blockStart : null,
      block_end_date:   blocksInventory ? blockEnd   : null,
    });
    onCreated(issue);
  };

  const fairnessCompensable = category === "maintenance" && blocksInventory;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="pms-hk-incident-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative bg-white dark:bg-stone-950 w-full sm:max-w-[540px] sm:rounded-lg rounded-t-2xl shadow-xl p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">
              Housekeeping · Reportar incidencia
            </div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100 mt-0.5">
              {villa?.villa_id} · {villa?.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="w-8 h-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <FieldLabel required>Categoría</FieldLabel>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    category === c.key
                      ? "bg-stone-900 text-white border-stone-900 dark:bg-stone-100 dark:text-stone-900 dark:border-stone-100"
                      : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-300"
                  }`}
                  data-testid={`pms-hk-incident-cat-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel required htmlFor="hk-incident-desc">Descripción</FieldLabel>
            <textarea
              id="hk-incident-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Ej: Grifo del baño principal gotea, requiere sustitución del cartucho."
              className="mt-1 w-full text-sm px-3 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
              data-testid="pms-hk-incident-desc"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="hk-incident-prio">Prioridad</FieldLabel>
              <select
                id="hk-incident-prio"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-1 w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
                data-testid="pms-hk-incident-prio"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel htmlFor="hk-incident-res">Reserva relacionada</FieldLabel>
              <input
                id="hk-incident-res"
                type="text"
                value={reservationId || ""}
                readOnly
                placeholder="—"
                className="mt-1 w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 text-stone-700 dark:text-stone-300 tabular-nums"
                data-testid="pms-hk-incident-res"
              />
              <div className="text-[10px] text-stone-400 mt-1">
                Preseleccionada desde la tarea de hoy si aplica.
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-stone-700 dark:text-stone-300 cursor-pointer">
            <input
              type="checkbox"
              checked={blocksInventory}
              onChange={(e) => setBlocksInventory(e.target.checked)}
              className="mt-0.5"
              data-testid="pms-hk-incident-blocks"
            />
            <span>
              Bloquea disponibilidad de la villa
              {blocksInventory && (
                <span className="block text-[11px] text-stone-500 dark:text-stone-400 mt-0.5">
                  Fairness compensable:{" "}
                  <span className="font-medium">
                    {fairnessCompensable ? "Sí" : "No"}
                  </span>{" "}
                  · se calcula automáticamente (categoría mantenimiento no imputable al owner).
                </span>
              )}
            </span>
          </label>

          {blocksInventory && (
            <div className="grid grid-cols-2 gap-3" data-testid="pms-hk-incident-block-dates">
              <div>
                <FieldLabel htmlFor="hk-incident-block-start" required>Bloquear desde</FieldLabel>
                <input
                  id="hk-incident-block-start"
                  type="date"
                  value={blockStart}
                  onChange={(e) => setBlockStart(e.target.value)}
                  className="mt-1 w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
                  data-testid="pms-hk-incident-block-start"
                />
              </div>
              <div>
                <FieldLabel htmlFor="hk-incident-block-end" required>Bloquear hasta</FieldLabel>
                <input
                  id="hk-incident-block-end"
                  type="date"
                  value={blockEnd}
                  onChange={(e) => setBlockEnd(e.target.value)}
                  min={blockStart}
                  className="mt-1 w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
                  data-testid="pms-hk-incident-block-end"
                />
                {!datesValid && (
                  <div className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                    La fecha de fin debe ser posterior al inicio.
                  </div>
                )}
              </div>
              <div className="col-span-2 text-[10px] text-stone-400">
                Se creará un bloqueo de disponibilidad tipo MAINTENANCE vinculado a esta incidencia
                y visible inmediatamente en Calendario y Allocation.
              </div>
            </div>
          )}

          <div>
            <FieldLabel>Foto</FieldLabel>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPhotoAttached((v) => !v)}
                className={`text-xs px-3 py-1.5 rounded border inline-flex items-center gap-1.5 ${
                  photoAttached
                    ? "bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200"
                    : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-300"
                }`}
                data-testid="pms-hk-incident-photo"
              >
                <Camera size={12} />
                {photoAttached ? "Foto adjunta (demo)" : "Adjuntar foto"}
              </button>
              <span className="text-[10px] text-stone-400">
                Placeholder visual · no hay upload real todavía.
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-700 dark:text-red-300">{error}</div>
        )}

        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="pms-hk-incident-submit"
          >
            <Plus size={12} weight="bold" /> Crear incidencia
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldLabel({ children, htmlFor, required }) {
  return (
    <label htmlFor={htmlFor} className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}
